import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/client"
import { formatDateLabel, formatMoney } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { ADMIN_TABLE_CLASSNAME, ADMIN_TABLE_HEAD_TH, ADMIN_TABLE_ROW, statusBadgeClass } from "@/lib/table"
import { ORDER_STATUS_LABELS, type OrderStatus } from "@/lib/types"

export function OrdersPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState("all")
  const [date, setDate] = useState("")
  const [q, setQ] = useState("")
  const filters = { date: date || undefined, status: status === "all" ? undefined : status }
  const orders = useQuery({
    queryFn: () => api.orders(filters),
    queryKey: queryKeys.orders(filters as Record<string, string>),
  })
  const rows = useMemo(() => {
    const list = orders.data?.orders ?? []
    const needle = q.trim().toLowerCase()
    if (!needle) return list
    return list.filter(
      (o) =>
        o.name.toLowerCase().includes(needle) ||
        (o.customerName ?? "").toLowerCase().includes(needle) ||
        (o.customerEmail ?? "").toLowerCase().includes(needle),
    )
  }, [orders.data, q])

  return (
    <div>
      <AdminPageHeader description="Weekly meal orders" title="Orders">
        <Input
          className="w-56"
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or email"
          value={q}
        />
        <Input className="w-40" onChange={(e) => setDate(e.target.value)} type="date" value={date} />
        <Select onValueChange={setStatus} value={status}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </AdminPageHeader>
      <div className="overflow-hidden rounded-lg border border-border">
        <Table className={ADMIN_TABLE_CLASSNAME}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Order</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Customer</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Week</TableHead>
              <TableHead className={`${ADMIN_TABLE_HEAD_TH} text-right`}>Total</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.isPending ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={5}>
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={5}>
                  No orders match these filters.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((order) => (
                <TableRow
                  className={ADMIN_TABLE_ROW}
                  key={order.id}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <TableCell className="font-medium">
                    <Link to={`/orders/${order.id}`}>{order.name}</Link>
                  </TableCell>
                  <TableCell>{order.customerName || order.customerEmail || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateLabel(order.weekStartDate)} – {formatDateLabel(order.weekEndDate)}
                  </TableCell>
                  <TableCell className="text-right">{formatMoney(order.amountTotal, order.currency)}</TableCell>
                  <TableCell>
                    <span className={statusBadgeClass(order.state)}>
                      {ORDER_STATUS_LABELS[order.state as OrderStatus] ?? order.state}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
